import { useState } from "react";
import { Eye } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import ConnectionsModal from "@/components/ConnectionsModal";

export const StatisticsTable = ({ data, onRefresh }) => {
  const [selectedDb, setSelectedDb] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewConnections = (db) => {
    setSelectedDb(db);
    setIsModalOpen(true);
  };

  const handleConnectionKilled = () => {
    // Close modal and refresh data
    setIsModalOpen(false);
    if (onRefresh) {
      onRefresh();
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "--";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Calculate total connections across all databases
  const totalConnections = data?.reduce((sum, db) => sum + (db.total || 0), 0) || 0;

  // Calculate percentage for a database
  const calculatePercentage = (dbTotal) => {
    if (!totalConnections || !dbTotal) return 0;
    return ((dbTotal / totalConnections) * 100).toFixed(1);
  };

  return (
    <>
      <Card className="border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="font-semibold">Database</TableHead>
                  <TableHead className="font-semibold text-center">% Capacity</TableHead>
                  <TableHead className="font-semibold text-center">Conexion</TableHead>
                  <TableHead className="font-semibold text-center">Idle</TableHead>
                  <TableHead className="font-semibold text-center">Active</TableHead>
                  <TableHead className="font-semibold text-center">Waiting</TableHead>
                  <TableHead className="font-semibold text-right">Memory (MB)</TableHead>
                  <TableHead className="font-semibold">Timestamp</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data && data.length > 0 ? (
                  data.sort((a, b) => b.total - a.total).map((db, index) => (
                    <TableRow key={index} className={`table-row-hover  ${db.total === 0 && 'bg-muted/50'}`}   >
                      <TableCell className="font-medium">
                        <div className="flex items-center space-x-2">
                          <div className="h-2 w-2 rounded-full bg-primary animate-pulse-subtle" />
                          <span className="mono-text">{db.name}</span>
                        </div>
                      </TableCell>

                      <TableCell className="text-center">
                        <Badge variant="outline" className="font-mono text-primary">
                          {calculatePercentage(db.total)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="font-mono">
                          {db.total}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="font-mono">
                          {db.idle}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant="outline"
                          className="font-mono bg-success/10 text-success border-success/30"
                        >
                          {db.active}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant="outline"
                          className="font-mono bg-warning/10 text-warning border-warning/30"
                        >
                          {db.waiting}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {db.memory?.toFixed(2) || "0.00"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground mono-text">
                        {formatTimestamp(db.timestamp)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewConnections(db)}
                          className="gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                      No database statistics available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {selectedDb && (
        <ConnectionsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          database={selectedDb}
          onConnectionKilled={handleConnectionKilled}
        />
      )}
    </>
  );
};

export default StatisticsTable;
